/**
 * src/lib/templates.js — đếm lượt tải mẫu, đọc/ghi thư viện mẫu dạng tệp.
 *
 * Vì sao đếm: số lượt tải mẫu là chỉ số cho biết nội dung có tạo giá trị thật
 * hay không, đáng tin hơn lượt xem trang. Ghi mỗi lượt thành một bản ghi để
 * Rules kiểm tra được; trình duyệt chỉ ghi, không đọc lại.
 *
 * Thất bại thì bỏ qua — người dùng đã có tệp, không có lý do gì để hiện lỗi.
 */
import { track } from "./track.js";
import { useEffect, useState } from 'react';
import { CO_CAU_HINH, layDb, layStorage } from './firebase.js';
import { TEMPLATES, SINH_SLUGS, templateBySlug } from '../data/templates.js';
import { chuanHoaMau, chuanHoaSlug, kiemTraMau, dinhDangCuaTen, tenTepTaiVe, DINH_DANG, TRAN_KICH_THUOC } from './templateModel.js';

export async function ghiNhanTaiMau(slug) {
  track("template_download", { template: String(slug).split(":")[0], kind: String(slug).endsWith(":en") ? "en" : "vi" });
  if (!CO_CAU_HINH) return;
  try {
    const db = await layDb();
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore/lite');
    await addDoc(collection(db, 'template_downloads'), { slug: String(slug).slice(0, 100), createdAt: serverTimestamp() });
  } catch { /* không ảnh hưởng tới việc tải tệp */ }
}

/** Tổng hợp lượt tải cho trang quản trị. */
export async function docLuotTaiMau(limit = 500) {
  const db = await layDb();
  if (!db) throw new Error('Website chưa cấu hình Firestore.');
  const { collection, getDocs, query, orderBy, limit: gioiHan } = await import('firebase/firestore/lite');
  const snap = await getDocs(query(collection(db, 'template_downloads'), orderBy('createdAt', 'desc'), gioiHan(limit)));
  const dem = new Map();
  snap.docs.forEach((d) => { const s = d.data().slug || '?'; dem.set(s, (dem.get(s) || 0) + 1); });
  return [...dem.entries()].map(([slug, soLuot]) => ({ slug, soLuot })).sort((a, b) => b.soLuot - a.soLuot);
}

/* ── Thư viện mẫu dạng tệp (/admin/mau) ───────────────────────────────────
 * Bản ghi nằm ở collection `templates`, tệp nằm ở Storage mau/<slug>/v<n>/.
 * Người đọc chỉ thấy mẫu PUBLISHED (Firestore Rules chặn phần còn lại).
 */
export async function docMau(slug) {
  const local = templateBySlug(slug);
  if (local || !CO_CAU_HINH) return local;
  try {
    const db = await layDb();
    const { doc, getDoc } = await import('firebase/firestore/lite');
    const snap = await getDoc(doc(db, 'templates', chuanHoaSlug(slug)));
    if (!snap.exists()) return null;
    const mau = chuanHoaMau({ ...snap.data(), slug: snap.id });
    return mau.status === 'PUBLISHED' ? mau : null;
  } catch { return null; }
}

/** Mẫu công khai: bản đóng gói lúc build + mẫu vừa xuất bản chưa kịp deploy. */
export async function docMauDaXuatBan() {
  if (!CO_CAU_HINH) return TEMPLATES;
  try {
    const db = await layDb();
    const { collection, getDocs, query, where } = await import('firebase/firestore/lite');
    const snap = await getDocs(query(collection(db, 'templates'), where('status', '==', 'PUBLISHED')));
    const merged = new Map(TEMPLATES.map((t) => [t.slug, t]));
    snap.docs.forEach((d) => { if (!SINH_SLUGS.includes(d.id)) merged.set(d.id, chuanHoaMau({ ...d.data(), slug: d.id })); });
    return [...merged.values()];
  } catch { return TEMPLATES; }
}

/** Quản trị: mọi mẫu dạng tệp, kể cả bản nháp. */
export async function docTatCaMau() {
  const db = await layDb();
  if (!db) throw new Error('Website chưa cấu hình Firestore.');
  const { collection, getDocs } = await import('firebase/firestore/lite');
  const snap = await getDocs(collection(db, 'templates'));
  return snap.docs.map((d) => chuanHoaMau({ ...d.data(), slug: d.id }));
}

export async function luuMau(raw, status) {
  const mau = chuanHoaMau({ ...raw, status });
  const loi = kiemTraMau(mau, status, SINH_SLUGS);
  if (loi.length) throw new Error(loi.join(' '));
  const db = await layDb();
  const { doc, setDoc, serverTimestamp } = await import('firebase/firestore/lite');
  const { updatedAt, ...ghi } = mau;
  if (status === 'PUBLISHED' && !ghi.publishedAt) ghi.publishedAt = new Date().toISOString().slice(0, 10);
  await setDoc(doc(db, 'templates', mau.slug), { ...ghi, updatedAt: serverTimestamp() });
  return { ...ghi, updatedAt: new Date().toISOString().slice(0, 10) };
}

/** Xoá bản ghi và mọi phiên bản tệp. Rules chỉ cho role admin. */
export async function xoaMau(slug) {
  const db = await layDb();
  if (!db) throw new Error('Website chưa cấu hình Firestore.');
  const { doc, deleteDoc } = await import('firebase/firestore/lite');
  await deleteDoc(doc(db, 'templates', chuanHoaSlug(slug)));
  try {
    const storage = await layStorage();
    const { ref, listAll, deleteObject } = await import('firebase/storage');
    const goc = await listAll(ref(storage, `mau/${chuanHoaSlug(slug)}`));
    for (const thuMuc of goc.prefixes) {
      const ben = await listAll(thuMuc);
      await Promise.all(ben.items.map((it) => deleteObject(it)));
    }
  } catch { /* bản ghi đã xoá; tệp mồ côi không ảnh hưởng người dùng */ }
}

/**
 * Tải tệp mẫu lên Storage ở đường dẫn phiên bản mới. Đặt sẵn Content-Disposition
 * để trình duyệt tải về đúng tên, thay vì mở tệp trong tab.
 */
export async function taiTepMau(file, slug, phienBan, ngonNgu = 'vi') {
  const format = dinhDangCuaTen(file?.name);
  if (!format) throw new Error('Chỉ nhận tệp .xlsx, .docx hoặc .pdf (không nhận .xlsm vì có macro).');
  if (file.size > TRAN_KICH_THUOC) throw new Error('Tệp lớn hơn 20 MB.');
  const s = chuanHoaSlug(slug);
  if (!s) throw new Error('Đặt tiêu đề hoặc đường dẫn trước khi tải tệp.');
  const storage = await layStorage();
  if (!storage) throw new Error('Website chưa cấu hình Firebase Storage.');
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  const name = tenTepTaiVe(s, phienBan, format, ngonNgu);
  const path = `mau/${s}/${ngonNgu === 'en' ? 'en-' : ''}v${phienBan}/${name}`;
  const r = ref(storage, path);
  await uploadBytes(r, file, {
    contentType: DINH_DANG[format].contentType,
    contentDisposition: `attachment; filename="${name}"`,
    cacheControl: 'public, max-age=31536000, immutable',
  });
  return { url: await getDownloadURL(r), path, name, size: file.size, format, phienBan };
}

/**
 * Lấy một mẫu theo slug: bản đóng gói có ngay (khớp HTML prerender), nếu chưa
 * có thì hỏi Firestore — cho mẫu vừa xuất bản mà chưa deploy lại.
 */
export function useMau(slug) {
  const [state, setState] = useState(() => {
    const m = slug ? templateBySlug(slug) : null;
    return { mau: m, loading: Boolean(slug) && !m };
  });
  useEffect(() => {
    if (!slug) { setState({ mau: null, loading: false }); return undefined; }
    const m = templateBySlug(slug);
    if (m) { setState({ mau: m, loading: false }); return undefined; }
    let live = true;
    setState({ mau: null, loading: true });
    docMau(slug).then((mau) => { if (live) setState({ mau, loading: false }); });
    return () => { live = false; };
  }, [slug]);
  return state;
}
